import React, { useMemo } from 'react';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useLocation } from 'react-router-dom';
import { useSettings } from 'services/settings/SettingsContext';
import { convertUTCToLocalTimeString } from 'internationalization/timezoneConversions';
import TimeRowForm from './TimeRowForm';
import { saveData } from 'services/api/saveData';
import { useGlobalContext } from 'GlobalContext';
import { useTranslation } from 'react-i18next';

const UserTimeRowList: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { setFloatingAlert } = useGlobalContext();
    const { userLocale, userTimezone, getTimeFormat } = useSettings();
    const params = new URLSearchParams(location.search);
    const userId = params.get("user_id") ?? undefined;
    const is12HourFormat = getTimeFormat() === '12h';

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'active',
            label: 'timesheets.general.timerows_filter_label',
            params: { 'deleted': false, assignee_id: userId }
        },
        {
            id: 'inactive',
            label: 'timesheets.general.deleted_timerows_filter_label',
            params: { 'deleted': true, assignee_id: userId }
        }
    ];

    // Specify form, detail modal or detailpage for this list
    const formModal = <TimeRowForm />;
    const onRowClick = 'editModal'

    // Specify the columns of this list
    const columns: ListColumnType[] = useMemo(() => [
        {
            width: "1.5fr",
            field: "assignee",
            label: "timesheets.general.assignee_name_label"
        },
        {
            width: "3fr",
            field: "job_info",
            label: "timesheets.general.project_or_job_label",
            render: (_, row) => renderJobInfo(row.project_name, row.job_number, row.ticket_number)
        },
        {
            width: "1.5fr",
            field: "time_type_name",
            label: "timesheets.general.time_type_label"
        },
        {
            width: "3fr",
            field: "time_range",
            label: "timesheets.general.hours_label",
            render: (_, row) => renderTimeRange(row)
        },
        {
            width: "144px",
            field: "status",
            label: 'timesheets.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status) 
        }
    ], []);

    // Render the project name, job number or ticket number
    const renderJobInfo = (project_name: any, job_number: any, ticket_number?: any) => {
        if (!project_name && !job_number && ticket_number) {
            return <span>{t('ticket.general.ticket_title', { number: ticket_number })}</span>;
        }

        if (!project_name && !job_number) {
            return <span>-</span>;
        }

        if (project_name && job_number) {
            return <span>{project_name} - {t('timerow.general.job_label')} {job_number}</span>;
        }

        if (project_name) {
            return <span>{project_name}</span>;
        }

        if (job_number) {
            return <span>{t('timerow.general.job_label')} {job_number}</span>;
        }

        return <span>-</span>;
    };

    // Format the duration
    const formatDuration = (hours: number) => {
        if (!hours) return "0:00";
        const totalMinutes = Math.round(hours * 60);
        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;
        return `${h}:${m.toString().padStart(2, '0')}`;
    };

    // Render the start and end time
    const renderTimeRange = (row: any) => {
        const duration = formatDuration(row.duration);
        const breakDuration = row.break_duration ? formatDuration(row.break_duration) : null;

        // Render just duration and break
        if (!row.start_datetime || !row.end_datetime) {
            return (
                <span>
                    {duration} {t('date_time.general.hour.singular')}
                    {breakDuration ? ` · ${t('timesheets.general.break_uncapitalized_label')} ${breakDuration}` : ''}
                </span>
            );
        }

        // Convert the start and end times to the local timezone
        const start = convertUTCToLocalTimeString(row.start_datetime, userLocale, userTimezone, is12HourFormat);
        const end = convertUTCToLocalTimeString(row.end_datetime, userLocale, userTimezone, is12HourFormat);

        // Render start, end, duration and break
        return (
            <span>
                {start} – {end} ({duration} {t('date_time.general.hour.singular')}{breakDuration ? `, ${t('timesheets.general.break_uncapitalized_label')} ${breakDuration}` : ''})
            </span>
        );
    };

    // Render status as badges
    const renderStatusBadge = (status: string) => {
        return (
            <div className='badge-container'>
                <div className={`status-badge timerow_${status}`}>
                    {t(`timerow.status.${status}`)}
                </div>                        
            </div>
        );
    };

    // Format the group date value
    const formatGroupDate = (dateString: string) => {
        const date = new Date(dateString)

        return date.toLocaleDateString(userLocale, {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric"
        })
    }

    return (
        <AbstractList
            query={{
                endpoint: 'get_user_timerow_list',
                defaultParams: {
                    deleted: false,
                    assignee_id: userId
                },
                object: 'timerow'
            }}
            objectName='timerow.general.object_name'
            filters={filters}
            columns={columns}
            formModal={formModal}
            onRowClick={onRowClick}
            showAddButton={false}
            isPaginated={false}
            groupByField={{
                field: "date",
                translateValue: false,
                formatValue: formatGroupDate,
            }}
            backButton={{
                title: 'timesheets.general.back_button_title',
                url: `/user-hours?week_start=${params.get("week_start")}`
            }}
            actionButton={{
                label: 'timesheets.general.approve_week_button',
                tooltip: 'timesheets.general.approve_week_tooltip',
                onClick: async (refetchList) => {
                    // Approve the timesheets
                    await saveData({ 
                        apiUrl: `approve_timesheet/${userId}/${params.get("week_start")}`,
                        data: {} 
                    })

                    // Show floating alert
                    setFloatingAlert({
                        type: 'success',
                        message: 'timesheets.general.week_approved_alert'
                    })

                    // Refresh the list
                    refetchList()
                }
            }}
            showPerWeek
        />
    );
};

export default UserTimeRowList;